import { get, off, ref, set } from "firebase/database";
import { listenDatabaseValue as onValue } from "../utils/listenDatabaseValue";
import { db } from "../lib/firebase";
import { getPathDatabaseByRole } from "./auth";
import { LaunchError } from "../utils/errors";
import { startUploadFileAws } from "../services/public";
import { canWriteCreativeTemplates } from "../features/creativeTemplates/utils/permissions";
import {
  FONT_KIND,
  buildFaceFromUpload,
  getFontMimeType,
  mergeFontCatalogs,
  normalizeFontName,
  sanitizeFamilyForRtdb,
  toFamilyRecordForSave,
  toFontKey,
  validateFontName,
} from "../features/creativeTemplates/utils/fonts";
import { SKIP_PERSIST_ITALIC } from "../features/creativeTemplates/utils/resolveFontFamilyIdentity";

const getPublicFontsPath = () => "fonts";

const getAdvertiserFontsPath = ({ advertiserID }) =>
  `fontsByAdvertiser/${advertiserID}`;

const getAdvertiserFontPath = ({ advertiserID, fontKey }) =>
  `${getAdvertiserFontsPath({ advertiserID })}/${fontKey}`;

const getFontUploadPath = ({ advertiserID }) =>
  `creative-fonts/${advertiserID}/`;

export const startListenerMergedFonts =
  ({
    onUpdatedDataCallback = () => {},
    onReferenceAvailable = () => {},
  } = {}) =>
  async (dispatch) => {
    try {
      const { userID: advertiserID } = dispatch(getPathDatabaseByRole());
      const commonRef = ref(db, getPublicFontsPath());
      const advertiserRef = advertiserID
        ? ref(db, getAdvertiserFontsPath({ advertiserID }))
        : null;

      let commonFonts = {};
      let advertiserFonts = {};

      const emit = () => {
        onUpdatedDataCallback(
          mergeFontCatalogs({ commonFonts, advertiserFonts })
        );
      };

      onReferenceAvailable({ commonRef, advertiserRef });

      onValue(
        commonRef,
        (snapshot) => {
          commonFonts = snapshot.val() || {};
          emit();
        },
        () => {
          commonFonts = {};
          emit();
        }
      );

      if (advertiserRef) {
        onValue(
          advertiserRef,
          (snapshot) => {
            advertiserFonts = snapshot.val() || {};
            emit();
          },
          () => {
            advertiserFonts = {};
            emit();
          }
        );
      } else {
        emit();
      }

      return true;
    } catch (error) {
      dispatch(
        LaunchError(error, false, [], {
          functionName: "startListenerMergedFonts",
          tags: { action: "fonts" },
          level: "error",
        })
      );
      onUpdatedDataCallback([]);
      return false;
    }
  };

export const stopListenerMergedFonts =
  ({ listenerRefs } = {}) =>
  async () => {
    if (listenerRefs?.commonRef) {
      await off(listenerRefs.commonRef);
    }
    if (listenerRefs?.advertiserRef) {
      await off(listenerRefs.advertiserRef);
    }
  };

export const startUploadAdvertiserFont =
  ({ name, file, parsed } = {}) =>
  async (dispatch) => {
    try {
      const { userID: advertiserID, role } = dispatch(getPathDatabaseByRole());
      if (!canWriteCreativeTemplates({ role })) {
        return { ok: false, reason: "unauthorized" };
      }

      const familyName = normalizeFontName({ name });
      const nameError = validateFontName({ name: familyName });
      if (nameError) {
        return { ok: false, reason: nameError };
      }

      const mimeType = getFontMimeType({ file });
      if (!mimeType) {
        return { ok: false, reason: "invalidFile" };
      }
      if (!advertiserID || !file) {
        return { ok: false, reason: "missingParameters" };
      }
      if (parsed?.skipPersist === SKIP_PERSIST_ITALIC) {
        return { ok: false, reason: "italicIgnored" };
      }

      const kind =
        parsed?.kind === FONT_KIND.VARIABLE
          ? FONT_KIND.VARIABLE
          : FONT_KIND.STATIC;
      let familyKey = toFontKey({ name: familyName });

      const url = await dispatch(
        startUploadFileAws(
          file,
          mimeType,
          getFontUploadPath({ advertiserID }),
          true,
          true
        )
      );
      if (!url) {
        return { ok: false, reason: "uploadFailed" };
      }

      const now = Date.now();
      const face = buildFaceFromUpload({ parsed: parsed || {}, url, mimeType });
      if (!face) {
        return { ok: false, reason: "uploadFailed" };
      }

      const namedInstances =
        kind === FONT_KIND.VARIABLE ? parsed?.namedInstances || {} : undefined;
      const axes = kind === FONT_KIND.VARIABLE ? parsed?.axes || null : null;

      const writeFamily = async (nextKey) => {
        const fontRef = ref(
          db,
          getAdvertiserFontPath({ advertiserID, fontKey: nextKey })
        );
        const existing = (await get(fontRef)).val() || {};
        return {
          fontRef,
          existing,
          result: toFamilyRecordForSave({
            existing,
            familyName,
            familyKey: nextKey,
            kind,
            face,
            namedInstances,
            axes,
            url,
            mimeType,
            now,
          }),
        };
      };

      let write = await writeFamily(familyKey);
      if (write.result.conflict) {
        familyKey = `${familyKey}_${kind}`;
        write = await writeFamily(familyKey);
      }
      if (write.result.conflict || !write.result.family) {
        return { ok: false, reason: "uploadFailed" };
      }

      const payload = sanitizeFamilyForRtdb(write.result.family);
      await set(write.fontRef, payload);

      return {
        ok: true,
        data: {
          key: familyKey,
          name: familyName,
          familyName,
          familyKey,
          faceKey: face.key,
          fontWeight: face.weight,
          kind,
          url,
          mimeType,
          family: write.result.family,
        },
      };
    } catch (error) {
      dispatch(
        LaunchError(error, true, [], {
          functionName: "startUploadAdvertiserFont",
          tags: { action: "fonts" },
          level: "error",
        })
      );
      return { ok: false, reason: "uploadFailed" };
    }
  };
